import _ from 'lodash';
import {
  ENV,
  ENV_ENUM,
  ID_BOOKMARK_TAB,
  TAB_KEY,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  TYPE_RR_HEART_MAP_ENUM,
  AUTO_EVENT_TAB_ENUM,
  AUTO_EVENTS,
  DEVICE_EVENT_TAB_ENUM,
  ECG_DISCLOSURE,
} from '@/shared/config';
import { TBeatHrActiveTab } from '@/shared/model';
import { NEED_AI_STARTED_PROCESSING_TABS, NEED_BEAT_DOWNLOADED_TABS } from './timeTracking.c';

type InitializingInfo = {
  isWsConnected?: boolean;
  isLoadedIndexDB?: boolean;
  isDownloadingBeats?: boolean;
  isInitializingStartAI?: boolean;
};

export type ActiveButton =
  | TBeatHrActiveTab
  | TYPE_RHYTHM_EVENT_ENUM
  | TYPE_BEAT_EVENT_ENUM
  | TYPE_RR_HEART_MAP_ENUM
  | string;

/**
 * Names the activity bucket that the current review time is charged to. Reports Initializing while the socket, IndexedDB, beat download or AI start-up the tab depends on is still pending, otherwise maps the tab and its selected action button onto a label — beat and rhythm types on Beats and HR, event types on ECG Events, device and auto event types on Strips Management, plus the fixed PDF report and HR Summary labels.
 *
 * @param activeTab - Identifier of the report tab currently in view.
 * @param actionButton - Selected event, beat or device sub-tab within that tab.
 * @param initializingInfo - Start-up flags carrying isWsConnected, isLoadedIndexDB, isDownloadingBeats and isInitializingStartAI.
 * @returns The tracking label for the current tab and sub-tab, Initializing while start-up runs, or N/A for an unknown tab.
 */
const generateTrackingType = (
  activeTab: string,
  actionButton: ActiveButton,
  initializingInfo: InitializingInfo,
) => {
  const { isWsConnected, isLoadedIndexDB, isDownloadingBeats, isInitializingStartAI } =
    initializingInfo || {};
  const downloadingBeats = isDownloadingBeats && NEED_BEAT_DOWNLOADED_TABS.includes(activeTab);
  const startingAI = isInitializingStartAI && NEED_AI_STARTED_PROCESSING_TABS.includes(activeTab);
  if (!isWsConnected || !isLoadedIndexDB || downloadingBeats || startingAI) {
    return 'Initializing';
  }
  switch (activeTab) {
    case TAB_KEY.BEATS: {
      switch (actionButton) {
        case TYPE_RHYTHM_EVENT_ENUM.ARTIFACT: {
          return 'Artifact areas';
        }
        case TYPE_RHYTHM_EVENT_ENUM.AFIB: {
          return 'AFib - Beats & HR page';
        }
        case TYPE_RR_HEART_MAP_ENUM.Q_TO_Q: {
          return 'Q - Q beats';
        }
        case TYPE_RR_HEART_MAP_ENUM.Q_TO_NSV: {
          return 'Q - N/S/V beats';
        }
        case TYPE_RR_HEART_MAP_ENUM.V: {
          return 'V beats';
        }
        case TYPE_RR_HEART_MAP_ENUM.S: {
          return 'S beats';
        }
        case TYPE_RR_HEART_MAP_ENUM.N: {
          return 'N beats';
        }
        default: {
          return 'Beats';
        }
      }
    }
    case TAB_KEY.ECG_EVENTS: {
      switch (actionButton) {
        case TYPE_BEAT_EVENT_ENUM.VE_COUPLET: {
          return 'VE Couplet - ECG Events';
        }
        case TYPE_BEAT_EVENT_ENUM.VE_RUN: {
          return 'VE Run - ECG Events';
        }
        case TYPE_BEAT_EVENT_ENUM.VE_BIGEMINY: {
          return 'VE Bigeminy - ECG Events';
        }
        case TYPE_BEAT_EVENT_ENUM.VE_TRIGEMINAL: {
          return 'VE Trigeminy - ECG Events';
        }
        case TYPE_BEAT_EVENT_ENUM.VE_QUADRIGEMINY: {
          return 'VE Quadrigeminy - ECG Events';
        }
        case TYPE_BEAT_EVENT_ENUM.SVE_COUPLET: {
          return 'SVE Couplet - ECG Events';
        }
        case TYPE_BEAT_EVENT_ENUM.SVE_RUN: {
          return 'SVE Run - ECG Events';
        }
        case TYPE_BEAT_EVENT_ENUM.SVE_BIGEMINY: {
          return 'SVE Bigeminy - ECG Events';
        }
        case TYPE_BEAT_EVENT_ENUM.SVE_TRIGEMINAL: {
          return 'SVE Trigeminy - ECG Events';
        }
        case TYPE_BEAT_EVENT_ENUM.SVE_QUADRIGEMINY: {
          return 'SVE Quadrigeminy - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.AFIB: {
          return 'AFib - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.TACHY: {
          return 'Tachy - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.BRADY: {
          return 'Brady - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.PAUSE: {
          return 'Pause - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.MAX_HR: {
          return 'Max HR - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.MIN_HR: {
          return 'Min HR - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.VT: {
          return 'VT - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.SVT: {
          return 'SVT - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.AVB2: {
          return '2nd Deg HB - ECG Events';
        }
        case TYPE_RHYTHM_EVENT_ENUM.AVB3: {
          return '3rd Deg HB - ECG Events';
        }
        case ID_BOOKMARK_TAB: {
          return 'Bookmarks - ECG Events';
        }
        default: {
          return 'ECG Events';
        }
      }
    }
    case TAB_KEY.STRIPS_MANAGEMENT: {
      switch (actionButton) {
        case DEVICE_EVENT_TAB_ENUM.NOTIFICATION: {
          return 'Included in report - Strips Management';
        }
        case DEVICE_EVENT_TAB_ENUM.PATIENT_TRIGGERED: {
          return 'Patient triggered - Strips Management';
        }
        case DEVICE_EVENT_TAB_ENUM.OTHER: {
          return 'Other - Device events - Strips Management';
        }
        case AUTO_EVENTS: {
          return 'Auto events - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.OTHER: {
          return 'Other - Auto events - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.VE: {
          return 'VE - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.SVE: {
          return 'SVE - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.AFIB: {
          return 'AFib - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.TACHY: {
          return 'Tachy - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.BRADY: {
          return 'Brady - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.PAUSE: {
          return 'Pause - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.VT: {
          return 'VT - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.SVT: {
          return 'SVT - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.MIN_HR: {
          return 'Min HR - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.MAX_HR: {
          return 'Max HR - Strips Management';
        }
        case AUTO_EVENT_TAB_ENUM.HB: {
          return 'HB - Strips Management';
        }
        case ECG_DISCLOSURE: {
          return 'ECG disclosure';
        }
        default: {
          return 'Strips Management';
        }
      }
    }
    case TAB_KEY.PDF_REPORT: {
      return 'PDF report';
    }
    case TAB_KEY.HR_SUMMARY: {
      return 'HR Summary';
    }
    default: {
      return 'N/A';
    }
  }
};

/**
 * Decides whether review activity is tracked for this user at all. Matches the user id against a per-environment allow list — a short QC list on staging, a longer reviewer list on customer, and nobody anywhere else.
 *
 * @param userId - Id of the signed-in user, matched against the environment's allow list.
 * @returns True when activity tracking is enabled for this user.
 */
const shouldTrackActivities = (userId: string) => {
  const allowedUserIds =
    ENV === ENV_ENUM.staging
      ? [
          '684681744e29af83bc89b7ab', // *: tam_qa_active (QC)
          '6715d3e6f9eea6dcac542647', // *: hangqa (QC)
          '685e1975d7572a5c5ee2dcd7', // *: huyencct (QC)
        ]
      : ENV === ENV_ENUM.customer
        ? [
            '686f2c9d4dcd1e5a40694016', // huyentech (QC)
            '682c423a979a82ce53322354', // hangqa (QC)
            '5fb149ecfa4acc085475a631', // psubash
            '612541ec0f3358c4187c1570', // skamaraj
            '64d0d90cc72a5c01b13093f5', // msangeetha
            '64d0d914c72a5c33223093fb', // psivasudha
            '64d0d8e4c72a5c70a43093d8', // mmanikandan
            '5fb14a0bfa4acceced75a632', // jramya
            '64d0d8f1c72a5c53143093e2', // pnithya
            '64d0d8f6c72a5ca1bb3093e5', // vprasanalakshmi
            '64d0d8c3c72a5c681e3093bf', // kayyappan
            '5fb146bafa4acc2ba875a628', // manoranjithamc
            '64d0d8dfc72a5ceb833093d4', // gkowsalya
            '5fb14ad6fa4accb2bd75a638', // mnivetha
            '612542660f335814587c1572', // epushparaj
            '64d0d8eec72a5ce58e3093e0', // npraba
            '64d0d8d6c72a5cbbdf3093cd', // jjaganathan
            '64d0d8bec72a5c8f5d3093bb', // kanandhan
            '64d0d8e9c72a5c0b3b3093dc', // kmithrarubini
            '67fee61ca43ee174eeb1bf16', // fchototech
            '5fb149bdfa4acc36e875a630', // jpachavelu
            '64d0d8d3c72a5c557b3093cb', // milavarasi
            '64e0165bc72a5c198c309520', // selvakumarm
            '64d0d915c72a5cb04a3093fc', // smani
            '5fb1472afa4acc5fb875a62b', // vimalak
          ]
        : [];
  return allowedUserIds.includes(userId);
};

/**
 * Decides whether the time-tracking debug toast is shown to this user. Returns false outside the staging environment, and on staging only for a hard-coded list of QC account ids.
 *
 * @param userId - Id of the signed-in user, matched against the allowed QC accounts.
 * @returns True when the tracking toast should be displayed for this user.
 */
const shouldShowTrackingToast = (userId: string) => {
  if (ENV !== ENV_ENUM.staging) return false;

  const allowedUserIds = [
    '684681744e29af83bc89b7ab', // tam_qa_active (QC)
    '63e9b37d1566ec5957d3a99a', // trqa (QC)
    '6715d3e6f9eea6dcac542647', // hangqa (QC)
  ];
  return allowedUserIds.includes(userId);
};

const generateCurrentActiveButton = ({
  activeTab,
  activeButtonBeats,
  activeButtonEcgEvents,
  activeButtonStripsManagement,
}: {
  activeTab: string;
  activeButtonBeats: ActiveButton;
  activeButtonEcgEvents: ActiveButton;
  activeButtonStripsManagement: ActiveButton;
}) =>
  activeTab === TAB_KEY.BEATS
    ? activeButtonBeats
    : activeTab === TAB_KEY.ECG_EVENTS
      ? activeButtonEcgEvents
      : activeTab === TAB_KEY.STRIPS_MANAGEMENT
        ? activeButtonStripsManagement
        : 'N/A';

const generateActiveButtonBeforeChanging = ({
  changedActiveButtonBeats,
  changedActiveButtonEcgEvents,
  changedActiveButtonStripsManagement,
  previousActiveButtonBeats,
  previousActiveButtonEcgEvents,
  previousActiveButtonStripsManagement,
}: {
  changedActiveButtonBeats: ActiveButton;
  changedActiveButtonEcgEvents: ActiveButton;
  changedActiveButtonStripsManagement: ActiveButton;
  previousActiveButtonBeats: ActiveButton;
  previousActiveButtonEcgEvents: ActiveButton;
  previousActiveButtonStripsManagement: ActiveButton;
}) => {
  const isDiffActiveButtonBeats = changedActiveButtonBeats !== previousActiveButtonBeats;
  const isDiffActiveButtonEcgEvents =
    changedActiveButtonEcgEvents !== previousActiveButtonEcgEvents;
  const isDiffActiveButtonStripsManagement =
    changedActiveButtonStripsManagement !== previousActiveButtonStripsManagement;
  return isDiffActiveButtonBeats
    ? previousActiveButtonBeats
    : isDiffActiveButtonEcgEvents
      ? previousActiveButtonEcgEvents
      : isDiffActiveButtonStripsManagement
        ? previousActiveButtonStripsManagement
        : 'N/A';
};

const generateDetectedActions = ({
  userId,
  mouseMoveCount,
  keyUpCount,
  scrollCount,
  untrustedMouseMoveCount,
  untrustedKeyUpCount,
  untrustedScrollCount,
  clickedIds,
  untrustedClickedIds,
  keyDownCount,
  untrustedKeyDownCount,
}: {
  userId: string;
  mouseMoveCount: number;
  keyUpCount: number;
  scrollCount: number;
  untrustedMouseMoveCount: number;
  untrustedKeyUpCount: number;
  untrustedScrollCount: number;
  clickedIds: string[];
  untrustedClickedIds: string[];
  keyDownCount: number;
  untrustedKeyDownCount: number;
}) => {
  let detectedActions = '';

  if (shouldTrackActivities(userId)) {
    const activities: string[] = [];

    if (mouseMoveCount > 0) {
      activities.push(`mouse:${mouseMoveCount}`);
    }

    if (keyUpCount > 0) {
      activities.push(`keyboard:${keyUpCount}`);
    }

    if (scrollCount > 0) {
      activities.push(`scroll:${scrollCount}`);
    }

    if (keyDownCount > 0) {
      activities.push(`keyboard-down:${keyDownCount}`);
    }

    if (untrustedKeyDownCount > 0) {
      activities.push(`untrusted-keyboard-down:${untrustedKeyDownCount}`);
    }

    if (untrustedMouseMoveCount > 0) {
      activities.push(`untrusted-mouse:${untrustedMouseMoveCount}`);
    }

    if (untrustedKeyUpCount > 0) {
      activities.push(`untrusted-keyboard:${untrustedKeyUpCount}`);
    }

    if (untrustedScrollCount > 0) {
      activities.push(`untrusted-scroll:${untrustedScrollCount}`);
    }

    // Combine ids that represent the same activity
    // e.g. 'hr-chart' and 'hr-chart-options' both represent interaction with the HR chart
    // so we combine them into 'hr-chart'
    const filteredClickedIds = clickedIds.map((id) =>
      ['hr-chart', 'hr-chart-options'].includes(id)
        ? 'hr-chart'
        : [
              'stripArtifactsBeatHr',
              'stripAfibsBeatHr',
              'stripBeatsBeatHr',
              'stripEventsRhythmEvents',
              'div-device-strip-management',
              'div-strip-management',
            ].includes(id)
          ? 'preview-strip'
          : id,
    );

    // Count occurrences of each activity
    const idCount = _.countBy(filteredClickedIds);
    const formattedClickedIds = Object.entries(idCount).map(([id, count]) => `${id}:${count}`);

    const filteredUntrustedClickedIds = untrustedClickedIds.map(() => 'untrusted-click');

    const untrustedIdCount = _.countBy(filteredUntrustedClickedIds);
    const formattedUntrustedClickedIds = Object.entries(untrustedIdCount).map(
      ([id, count]) => `${id}:${count}`,
    );

    // Combine trusted and untrusted clicked ids
    const combinedFilteredClickedIds = [
      ...activities,
      ...formattedClickedIds,
      ...formattedUntrustedClickedIds,
    ];

    if (combinedFilteredClickedIds.length > 0) {
      detectedActions = combinedFilteredClickedIds.join(';');
    }
  }

  return detectedActions;
};

/**
 * Decides whether the report tab has finished starting up so its review time may be counted. Always demands a live websocket and a loaded IndexedDB, additionally requires beat download to be finished on the tabs that need beats, and also requires AI start-up to be finished on the tabs that need AI processing.
 *
 * @param initializingInfo - Start-up flags carrying isWsConnected, isLoadedIndexDB, isDownloadingBeats and isInitializingStartAI.
 * @param activeTab - Identifier of the report tab whose start-up requirements are applied.
 * @returns True once every start-up step that tab depends on has completed.
 */
const checkFinishedInitializing = (initializingInfo: InitializingInfo, activeTab: string) => {
  const { isWsConnected, isLoadedIndexDB, isDownloadingBeats, isInitializingStartAI } =
    initializingInfo || {};

  if (NEED_AI_STARTED_PROCESSING_TABS.includes(activeTab)) {
    return isWsConnected && isLoadedIndexDB && !isDownloadingBeats && !isInitializingStartAI;
  }
  if (NEED_BEAT_DOWNLOADED_TABS.includes(activeTab)) {
    return isWsConnected && isLoadedIndexDB && !isDownloadingBeats;
  }
  return isWsConnected && isLoadedIndexDB; // tab 4
};

export {
  generateActiveButtonBeforeChanging,
  generateCurrentActiveButton,
  generateDetectedActions,
  generateTrackingType,
  shouldShowTrackingToast,
  checkFinishedInitializing,
};
